import { Component } from '@theme/component';
//import { isClickedOutside, normalizeString, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that displays a localization form.
 *
 * @typedef {object} FormRefs
 * @property {HTMLSelectElement} countrySelect - The country select element.
 * @property {HTMLSelectElement} languageSelect - The language select element.
 * @property {HTMLScriptElement} jsonScript - The json script element.
 * @property {HTMLFormElement} form - The form element.
 * @property {HTMLImageElement} [countryFlag] - The country flag image element.
 *
 * @extends {Component<FormRefs>}
 */
export class LocalizationForm extends Component {
  requiredRefs = ['countrySelect', 'languageSelect', 'jsonScript', 'form'];

  connectedCallback() {
    super.connectedCallback();

    const data = JSON.parse(this.refs.jsonScript.textContent);
    this.translations = data.translations;
    this.countries = data.countries;
    this.refs.countrySelect.addEventListener('change', this.handleCountryChange.bind(this));
    this.refs.form.addEventListener('submit', this.redirectToExternalStore.bind(this));
    this.submitByURL();
    this.checkSuggestions();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  /**
   * Handles country select change - updates languages, flag, and country info.
   *
   * @param {Event} event - The event object.
   */
  handleCountryChange(event) {
    const countryData = this.countries[event.target.value];

    this.refs.languageSelect.innerHTML = countryData.languages
      .map(
        (language) => `
      <option value="${language.iso_code}" ${language.primary === 'true' ? 'selected' : ''}>
        ${language.endonym_name}
      </option>
    `,
      )
      .join('');

    const countryFlag = this.querySelector('.localization-form__flag');
    const globeIcon = this.querySelector('.localization-form__select-wrapper .icon-globe');

    // Only toggle flag/globe if flag element exists (flags setting is enabled)
    if (countryFlag) {
      if (countryData.flag) {
        countryFlag.src = countryData.flag;
        countryFlag.style.display = '';
        if (globeIcon) globeIcon.style.display = 'none';
      } else {
        countryFlag.style.display = 'none';
        if (globeIcon) globeIcon.style.display = '';
      }
    }

    this.updateCountryInfo(countryData);
    this.updateButton(countryData);
  }

  /**
   * Updates the submit button text with the selected country name.
   *
   * @param {object} countryData - The country data object.
   */
  updateButton(countryData) {
    if (this.refs.submitButton && this.translations.button) {
      this.refs.submitButton.textContent = this.translations.button.replace('[country]', countryData.name);
    }
  }

  /**
   * Updates the country info box with the selected country's information.
   *
   * @param {object} countryData - The country data object.
   */
  updateCountryInfo(countryData) {
    const infoBox = this.querySelector('.localization-form__country-info-box');
    const warningEl = this.querySelector('.localization-form__country-info-warning');

    const info = countryData.info;

    if (!info) {
      if (infoBox) infoBox.style.display = 'none';
      if (warningEl) warningEl.style.display = 'none';
      return;
    }

    if (infoBox) {
      infoBox.style.display = '';

      const infoFlag = infoBox.querySelector('.localization-form__country-info-flag');
      const infoGlobe = infoBox.querySelector('.localization-form__country-info-globe');
      const infoTitle = infoBox.querySelector('.localization-form__country-info-title');
      const infoList = infoBox.querySelector('.localization-form__country-info-list');

      if (countryData.flag) {
        if (infoFlag) {
          infoFlag.src = countryData.flag;
          infoFlag.style.display = '';
        }
        if (infoGlobe) infoGlobe.style.display = 'none';
      } else {
        if (infoFlag) infoFlag.style.display = 'none';
        if (infoGlobe) infoGlobe.style.display = '';
      }

      if (infoTitle) {
        infoTitle.textContent = this.translations.infoHeading.replace('[country]', countryData.name);
      }

      if (infoList) {
        infoList.innerHTML =
          info.lines && info.lines.length ? info.lines.map((line) => `<li>${line}</li>`).join('') : '';
      }
    }

    if (warningEl) {
      if (info.warning) {
        warningEl.textContent = info.warning;
        warningEl.style.display = '';
      } else {
        warningEl.style.display = 'none';
      }
    }
  }

  /**
   * Redirects to the external store.
   *
   * @param {Event} event - The event object.
   */
  redirectToExternalStore(event) {
    const selectedCountryHost = this.refs.countrySelect.selectedOptions[0]?.dataset.host || '';

    if (!selectedCountryHost) return;

    event.preventDefault();
    window.location.href = `https://${selectedCountryHost}?country_code=${this.refs.countrySelect.value}&language_code=${this.refs.languageSelect.value}`;
  }

  /**
   * Preselects the country and language in the form.
   * @param {string} countryCode - The country code to preselect.
   * @param {string} languageCode - The language code to preselect.
   */
  preselect(countryCode, languageCode) {
    if (
      !countryCode ||
      !Array.from(this.refs.countrySelect.options).find((option) => option.value === countryCode) ||
      (this.refs.countrySelect.value === countryCode && this.refs.languageSelect.value === languageCode)
    )
      return;

    this.refs.countrySelect.value = countryCode;
    this.refs.countrySelect.dispatchEvent(new Event('change'));

    if (!languageCode || !Array.from(this.refs.languageSelect.options).find((option) => option.value === languageCode))
      return;

    this.refs.languageSelect.value = languageCode;
  }

  /**
   * Submits the form by URL parameters.
   */
  submitByURL() {
    const url = new URL(window.location.href);
    const countryCode = url.searchParams.get('country_code');
    const languageCode = url.searchParams.get('language_code');

    if (countryCode && languageCode) {
      url.searchParams.delete('country_code');
      url.searchParams.delete('language_code');
      history.replaceState({}, '', url.toString());

      // Visitor already picked a market on another domain — don't prompt again on this one.
      localStorage.setItem('localization_suggestions_checked', 'true');
      this.preselect(countryCode, languageCode);
      this.refs.form.submit();
    }
  }

  /**
   * Checks for suggestions and preselects the country and language if found.
   */
  async checkSuggestions() {
    if (localStorage.getItem('localization_suggestions_checked')) return;
    // Set before the fetch so a reload/second init while it's in flight can't prompt again.
    localStorage.setItem('localization_suggestions_checked', 'true');

    const { detected_values } = await fetch('/browsing_context_suggestions.json').then((r) => r.json());
    const localizationModal = document.getElementById('localization-modal');
    const detectedCountry = detected_values?.country?.handle;
    // Server-rendered `selected` option = the market the visitor is already in.
    const currentCountry = this.refs.countrySelect.querySelector('option[selected]')?.value;

    // Only prompt when the visitor is in the wrong market. Also keeps it from reopening when a
    // consent tool (e.g. Cookiebot auto-blocking) wipes the localStorage flag above.
    if (
      !detectedCountry ||
      detectedCountry === currentCountry ||
      !localizationModal ||
      !this.countries[detectedCountry]
    )
      return;

    this.preselect(detectedCountry, '');
    localizationModal.showDialog();
  }
}

if (!customElements.get('localization-form')) {
  customElements.define('localization-form', LocalizationForm);
}
